<div class="vendor-registration-container">
  <!-- Step 1: Company Information -->
  <div *ngIf="currentStep === 1" class="registration-card">
    <div class="card-header">
      <div class="header-content">
        <i class="ri-building-line header-icon"></i>
        <h2 class="header-title">Vendor Registration Portal</h2>
      </div>
      <p class="header-subtitle">Complete your vendor profile and documentation</p>
    </div>

    <div class="progress-indicator">
      <div class="step" [class.active]="currentStep >= 1" [class.completed]="currentStep > 1">
        <span class="step-number">1</span>
      </div>
      <div class="step" [class.active]="currentStep >= 2" [class.completed]="currentStep > 2">
        <span class="step-number">2</span>
      </div>
      <div class="step" [class.active]="currentStep >= 3" [class.completed]="currentStep > 3">
        <span class="step-number">3</span>
      </div>
    </div>

    <form [formGroup]="companyInfoForm" (ngSubmit)="submitCompanyInfo()" class="registration-form">
      <div class="form-section">
        <h3 class="section-title">Company Information</h3>
        
        <div class="form-row">
          <div class="form-group">
            <label for="companyName" class="form-label">Company Name</label>
            <input 
              type="text" 
              id="companyName" 
              formControlName="companyName" 
              class="form-control"
              placeholder="Enter company name">
          </div>
          
          <div class="form-group">
            <label for="taxId" class="form-label">Tax ID / EIN</label>
            <input 
              type="text" 
              id="taxId" 
              formControlName="taxId" 
              class="form-control"
              placeholder="XX-XXXXXXX">
          </div>
        </div>

        <div class="form-row">
          <div class="form-column-left">
            <div class="form-group">
              <label for="businessAddress" class="form-label">Business Address *</label>
              <textarea 
                id="businessAddress" 
                formControlName="businessAddress" 
                class="form-control"
                rows="3"
                placeholder="Enter complete business address">
              </textarea>
            </div>
          </div>
          
          <div class="form-column-right">
            <div class="form-group">
              <label class="form-label">Primary Contact</label>
              <input 
                type="text" 
                formControlName="primaryContact" 
                class="form-control"
                placeholder="Contact Name">
              <input 
                type="email" 
                id="email" 
                formControlName="email" 
                class="form-control"
                placeholder="Email Address">
              <input 
                type="tel" 
                id="phoneNumber" 
                formControlName="phoneNumber" 
                class="form-control"
                placeholder="Phone Number">
            </div>
          </div>
        </div>

        <div class="form-group">
          <label for="businessType" class="form-label">Business Type *</label>
          <select 
            id="businessType" 
            formControlName="businessType" 
            class="form-control">
            <option value="">Select business type</option>
            <option value="Corporation">Corporation</option>
            <option value="LLC">LLC</option>
            <option value="Partnership">Partnership</option>
            <option value="Sole Proprietorship">Sole Proprietorship</option>
            <option value="Non-Profit">Non-Profit</option>
          </select>
        </div>
      </div>

      <div class="form-actions" style="text-align:center;">
        <button type="submit" class="btn btn-secondary" style="background-color:#18479f; border-color:#18479f; color:#fff;" [disabled]="isLoading">
          Continue to Documents <i class="ri-arrow-right-line"></i>
        </button>
      </div>
    </form>
  </div>

  <!-- Step 2: Document Upload -->
  <div *ngIf="currentStep === 2" class="registration-card">
    <div class="card-header">
      <div class="header-content">
        <h2 class="header-title">Required Documentation</h2>
      </div>
      <p class="header-subtitle">Upload required documents for approval</p>
    </div>

    <div class="progress-indicator">
      <div class="step" [class.active]="currentStep >= 1" [class.completed]="currentStep > 1">
        <span class="step-number">1</span>
      </div>
      <div class="step" [class.active]="currentStep >= 2" [class.completed]="currentStep > 2">
        <span class="step-number">2</span>
      </div>
      <div class="step" [class.active]="currentStep >= 3" [class.completed]="currentStep > 3">
        <span class="step-number">3</span>
      </div>
    </div>

    <form [formGroup]="documentForm" (ngSubmit)="submitDocuments()" class="document-form">
      <div class="document-row">
        <!-- Banking Information -->
        <div class="document-section">
          <h3 class="section-title">Banking Information</h3>
          <div class="upload-area" 
               (dragover)="$event.preventDefault()" 
               (drop)="onFileDropped($event, 'VoidedCheck')"
               (click)="voidedCheckInput.click()">
            <div class="upload-content" *ngIf="!getDocumentByType('VoidedCheck')">
              <i class="ri-bank-line upload-icon banking-icon"></i>
              <h4>Upload Voided Check or Bank Letter</h4>
              <p>PDF, JPG, PNG (Max 5MB)</p>
            </div>
            <div class="document-preview" *ngIf="getDocumentByType('VoidedCheck')">
              <div class="preview-content">
                <div class="preview-info">
                  <span class="document-name">{{ getDocumentByType('VoidedCheck').filename }}</span>
                </div>
                <div class="preview-actions">
                  <button type="button" class="btn-remove" (click)="removeDocument('VoidedCheck', $event)">
                    <i class="ri-close-line"></i>
                  </button>
                </div>
              </div>
            </div>
            <input #voidedCheckInput type="file" class="file-input" 
                   (change)="onFileSelected($event, 'VoidedCheck')" 
                   accept=".pdf,.jpg,.jpeg,.png">
          </div>
          
          <div class="form-group">
            <label for="accountType" class="form-label">Account Type</label>
            <select id="accountType" formControlName="accountType" class="form-control">
              <option value="Checking">Checking</option>
              <option value="Savings">Savings</option>
              <option value="Business">Business</option>
            </select>
          </div>
        </div>

        <!-- Certificate of Insurance -->
        <div class="document-section">
          <h3 class="section-title">Certificate of Insurance</h3>
          <div class="upload-area" 
               (dragover)="$event.preventDefault()" 
               (drop)="onFileDropped($event, 'COI')"
               (click)="coiInput.click()">
            <div class="upload-content" *ngIf="!getDocumentByType('COI')">
              <i class="ri-shield-check-line upload-icon insurance-icon"></i>
              <h4>Upload Current COI</h4>
              <p>Must be current and valid</p>
            </div>
            <div class="document-preview" *ngIf="getDocumentByType('COI')">
              <div class="preview-content">
                <div class="preview-info">
                  <span class="document-name">{{ getDocumentByType('COI').filename }}</span>
                </div>
                <div class="preview-actions">
                  <button type="button" class="btn-remove" (click)="removeDocument('COI', $event)">
                    <i class="ri-close-line"></i>
                  </button>
                </div>
              </div>
            </div>
            <input #coiInput type="file" class="file-input" 
                   (change)="onFileSelected($event, 'COI')" 
                   accept=".pdf,.jpg,.jpeg,.png">
          </div>
          
          <div class="form-group">
            <label for="insuranceExpDate" class="form-label">Insurance Expiration Date</label>
            <input type="date" id="insuranceExpDate" formControlName="insuranceExpDate" class="form-control">
          </div>
        </div>
      </div>

      <!-- Bottom Row: Additional Documents -->
      <div class="document-section">
        <h3 class="section-title">Additional Documents</h3>
        <div class="upload-area" 
             (dragover)="$event.preventDefault()" 
             (drop)="onFileDropped($event, 'Additional')"
             (click)="additionalInput.click()">
          <div class="upload-content" *ngIf="getAdditionalDocuments().length === 0">
            <i class="ri-attachment-line upload-icon additional-icon"></i>
            <h4>W-9, Business License, or Other Documents</h4>
            <p>Optional - Upload any additional required documents</p>
          </div>
          <div class="additional-documents-list" *ngIf="getAdditionalDocuments().length > 0">
            <div class="document-preview" *ngFor="let doc of getAdditionalDocuments(); let i = index">
              <div class="preview-content">
                <div class="preview-info">
                  <span class="document-name">{{ doc.filename }}</span>
                </div>
                <div class="preview-actions">
                  <button type="button" class="btn-remove" (click)="removeAdditionalDocument(i, $event)">
                    <i class="ri-close-line"></i>
                  </button>
                </div>
              </div>
            </div>
            <div class="add-more-docs" (click)="additionalInput.click()">
              <i class="ri-add-line"></i>
              <span>Add more documents</span>
            </div>
          </div>
          <input #additionalInput type="file" class="file-input" 
                 (change)="onFileSelected($event, 'Additional')" 
                 accept=".pdf,.jpg,.jpeg,.png" multiple>
        </div>
      </div>


      <div class="form-actions">
        <button type="button" class="btn btn-secondary" (click)="previousStep()">
          <i class="ri-arrow-left-line"></i> Back
        </button>
        <button type="submit" class="btn btn-secondary"  [disabled]="isLoading" style="background-color:#18479f; border-color:#18479f; color:#fff;">
          Submit for Approval <i class="ri-arrow-right-line"></i>
        </button>
      </div>
    </form>
  </div>

  <!-- Step 3: Registration Status -->
  <div *ngIf="currentStep === 3" class="registration-card">
    <div class="card-header">
      <div class="header-content">
        <i class="ri-bar-chart-line header-icon"></i>
        <h2 class="header-title">Registration Status</h2>
      </div>
      <p class="header-subtitle">Track your vendor approval progress</p>
    </div>

    <div class="progress-indicator">
      <div class="step" [class.active]="currentStep >= 1" [class.completed]="currentStep > 1">
        <span class="step-number">1</span>
      </div>
      <div class="step" [class.active]="currentStep >= 2" [class.completed]="currentStep > 2">
        <span class="step-number">2</span>
      </div>
      <div class="step" [class.active]="currentStep >= 3" [class.completed]="currentStep > 3">
        <span class="step-number">3</span>
      </div>
    </div>

    <div class="status-content">
      <!-- Pending Review Alert -->
      <div *ngIf="registrationStatus === 'pending'" class="alert alert-warning">
        <i class="ri-time-line alert-icon"></i>
        <div class="alert-content">
          <h4>Pending Review</h4>
          <p>Your documents are currently under review by our AP specialist team. We'll notify you once the review is complete.</p>
        </div>
      </div>

      <!-- Approved Status -->
      <div *ngIf="registrationStatus === 'approved'" class="alert alert-success">
        <i class="ri-check-line alert-icon"></i>
        <div class="alert-content">
          <h4>Registration Approved</h4>
          <p>Congratulations! Your vendor registration has been approved. You can now start using our platform.</p>
        </div>
      </div>

      <!-- Submitted Documents -->
      <div class="submitted-documents">
        <h3>Submitted Documents</h3>
        <div class="document-status-list">
          <div *ngFor="let doc of documentStatuses" class="document-status-item">
            <div class="document-info">
              <h5>{{ doc.type }}</h5>
              <p>Submitted: {{ doc.submittedDate }}</p>
            </div>
            <div class="document-status">
              <i [class]="getStatusIcon(doc.status)" [ngClass]="getStatusColor(doc.status)"></i>
              <span [ngClass]="getStatusColor(doc.status)">{{ doc.status | titlecase }}</span>
            </div>
          </div>
        </div>
      </div>

      <!-- Additional Information -->
      <div class="additional-info">
        <h3>Need to provide additional information?</h3>
        <p>If our team requires additional documentation, you'll receive an email with specific instructions.</p>
        <button class="btn btn-outline-secondary">
          <i class="ri-customer-service-line"></i> Contact Support
        </button>
      </div>

      <!-- Simulate Approval Button (for testing) -->
      <div class="form-actions" style="text-align:center;">
        <button *ngIf="registrationStatus === 'pending'" class="btn btn-primary" (click)="simulateApproval()" style="width: 30%;">
          Simulate Approval <i class="ri-arrow-right-line"></i>
        </button>
      </div>
    </div>
  </div>

  <!-- Loading Overlay -->
  <div *ngIf="isLoading" class="loading-overlay">
    <div class="loading-spinner">
      <i class="ri-loader-4-line"></i>
      <p>Processing...</p>
    </div>
  </div>
</div>
